import { MVTLayer } from '@deck.gl/geo-layers';
import { GeoJsonLayer, TextLayer, IconLayer } from '@deck.gl/layers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPureWirelessVisible } from '@rsos/reduxDataStore/src/features/additionalLayers/additionalLayersSlice';
import {
  setCardsToRender,
  setMapCardData,
  closeCard,
  setPureWirelessFloor,
  PURE_WIRELESS_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import {
  whichElevationService,
  type MapElevationProvider,
} from '../../../../utils/elevation/whichElevationService';
import {
  MVT_SIMPLE_FILL_COLOR,
  MVT_SIMPLE_LINE_COLOR,
  HIGHLIGHT_COLOR,
  CALL_FLOOR_HIGHLIGHT_COLOR,
  CALL_FLOOR_HIGHLIGHT_WIDTH,
  SELECTED_ROOM_HIGHLIGHT,
  SELECTED_ROOM_OUTLINE,
  FOOTPRINT_OUTLINE_COLOR,
  FOOTPRINT_FILL_COLOR,
  OUTDOOR_POINT_LAYER_NAME,
  OUTDOOR_LABEL_SHADOW,
  PURE_WIRELESS_URL,
  ROOM_LABEL_MIN_ZOOM,
  POINT_MARKER_MIN_ZOOM,
  POINT_LABEL_MIN_ZOOM,
  POINT_MARKER_TRIANGLE_ICON,
  INDOOR_POINT_MARKER_ICON,
} from './constants';
import {
  getPolygonCentroid,
  getFeatureLabel,
  getRoomFillColor,
  getBuildingKey,
  fetchBuildingGeoJSON,
  isPointInPolygon,
  estimateFloor,
} from './helpers';
import type {
  FloorInfo,
  TileProperties,
  PolygonFeature,
  PointFeature,
  TileFeature,
  MapCardState,
  BuildingGeoJSONData,
  DeckPickingInfo,
  DeckLayerWithId,
  MVTTile,
} from './types';

export interface HoverInfo {
  x: number;
  y: number;
  object: TileFeature | GeoJSON.Feature;
}

export interface PureWirelessState {
  selectedBuilding: string | null;
  isDetailMode: boolean;
  hoverInfo: HoverInfo | null;
  selectedFloor: number | null;
  isVisible: boolean;
  isCardOpen: boolean;
  geoJSONData: BuildingGeoJSONData | null;
  isLoadingGeoJSON: boolean;
}

// Cache for fetched GeoJSON data by siteId to avoid repeated network requests
const geoJSONCache = new Map<string, BuildingGeoJSONData>();

export const TOOLTIP_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 1000,
  pointerEvents: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  padding: '8px 12px',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
  whiteSpace: 'nowrap',
  margin: '-10px',
};

export { MVT_SIMPLE_FILL_COLOR, MVT_SIMPLE_LINE_COLOR, HIGHLIGHT_COLOR };

// Type for emergency calls state
interface EmergencyCallsState {
  emergencyCalls: {
    selectedCallerID: string | null;
    callQueue: Record<string, unknown>;
  };
}

export interface UsePureWirelessCoreOptions {
  mapProvider: MapElevationProvider;
}

export const usePureWirelessCore = (options: UsePureWirelessCoreOptions) => {
  const { mapProvider } = options;
  const dispatch = useDispatch();

  const getGroundElevation = useMemo(
    () => whichElevationService(mapProvider),
    [mapProvider],
  );

  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [isDetailMode, setIsDetailMode] = useState(false);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [geoJSONData, setGeoJSONData] = useState<BuildingGeoJSONData | null>(
    null,
  );
  const [isLoadingGeoJSON, setIsLoadingGeoJSON] = useState(false);
  const [groundElevation, setGroundElevation] = useState<number | null>(null);
  const [isEstimatingFloor, setIsEstimatingFloor] = useState(false);
  const [isCallInsideBuilding, setIsCallInsideBuilding] = useState(false);
  const [estimatedCallFloor, setEstimatedCallFloor] = useState<number | null>(
    null,
  );
  const [selectedRoomFeature, setSelectedRoomFeature] =
    useState<GeoJSON.Feature | null>(null);

  const buildingFloorsRef = useRef<Map<string, Map<number, FloorInfo>>>(
    new Map(),
  );

  const isVisible = useSelector(selectPureWirelessVisible);
  const isCardOpen = useSelector(
    (state: { mapCard: MapCardState }) =>
      state.mapCard.cards[PURE_WIRELESS_CARD] ?? false,
  );
  const reduxSelectedFloor = useSelector(
    (state: { mapCard: MapCardState }) =>
      state.mapCard.mapCardData?.selectedFloor,
  );
  const selectedFloor = reduxSelectedFloor ?? null;

  // Access call data for floor estimation
  const selectedCallerID = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls.selectedCallerID,
  );
  const callQueue = useSelector(
    (state: EmergencyCallsState) => state.emergencyCalls.callQueue,
  );
  const selectedCall = selectedCallerID ? callQueue[selectedCallerID] : null;
  const mostRecentLocation = getMostRecentLoc(
    selectedCall as Parameters<typeof getMostRecentLoc>[0],
  );

  useEffect(() => {
    if (!isCardOpen) {
      setIsDetailMode(false);
      setSelectedBuilding(null);
      setGeoJSONData(null);
      setSelectedRoomFeature(null);
    }
  }, [isCardOpen]);

  useEffect(() => {
    if (isDetailMode) {
      setHoverInfo(null);
    } else {
      setSelectedRoomFeature(null);
    }
  }, [isDetailMode]);

  useEffect(() => {
    return () => {
      dispatch(closeCard(PURE_WIRELESS_CARD));
    };
  }, [dispatch]);

  // Floor estimation effect: when we have geoJSONData and a call with altitude,
  // check if the call is inside the building and estimate the floor
  useEffect(() => {
    const performFloorEstimation = async () => {
      // Reset state when conditions aren't met
      if (
        !isDetailMode ||
        !geoJSONData?.footprint?.features ||
        !mostRecentLocation
      ) {
        setIsCallInsideBuilding(false);
        setEstimatedCallFloor(null);
        return;
      }

      const callLat = (mostRecentLocation as { latitude?: number }).latitude;
      const callLng = (mostRecentLocation as { longitude?: number }).longitude;
      const callAltitude = (mostRecentLocation as { altitude?: number })
        .altitude;
      const verticalUncertainty =
        (mostRecentLocation as { altitude_uncertainty_radius?: number })
          .altitude_uncertainty_radius ?? 0;

      // Need valid coordinates and altitude
      if (
        callLat === undefined ||
        callLng === undefined ||
        callAltitude === undefined
      ) {
        setIsCallInsideBuilding(false);
        setEstimatedCallFloor(null);
        return;
      }

      const footprintFeatures = geoJSONData.footprint.features;

      // Check if call is inside any footprint
      const isInsideBuilding = footprintFeatures.some(feature => {
        const geometry = feature.geometry as
          | GeoJSON.Polygon
          | GeoJSON.MultiPolygon;
        return isPointInPolygon(callLat, callLng, geometry);
      });

      if (!isInsideBuilding) {
        setIsCallInsideBuilding(false);
        setEstimatedCallFloor(null);
        return;
      }

      setIsEstimatingFloor(true);

      try {
        // Get ground elevation if we don't have it or location changed
        let elevation = groundElevation;
        if (elevation === null) {
          elevation = await getGroundElevation(callLat, callLng);
          setGroundElevation(elevation);
        }

        if (elevation === null) {
          setIsCallInsideBuilding(false);
          setEstimatedCallFloor(null);
          return;
        }

        // Estimate the floor
        const result = estimateFloor(
          callAltitude,
          elevation,
          footprintFeatures,
          verticalUncertainty,
        );

        if (result) {
          // Auto-select the estimated floor and mark call as inside building
          dispatch(setPureWirelessFloor(result.estimatedFloor));
          setIsCallInsideBuilding(true);
          setEstimatedCallFloor(result.estimatedFloor);
        } else {
          setIsCallInsideBuilding(false);
          setEstimatedCallFloor(null);
        }
      } finally {
        setIsEstimatingFloor(false);
      }
    };

    performFloorEstimation();
  }, [
    isDetailMode,
    geoJSONData,
    mostRecentLocation,
    groundElevation,
    getGroundElevation,
    dispatch,
  ]);

  // Sync estimatedCallFloor into mapCardData whenever it changes.
  // The initial setMapCardData (in handleClick) fires before the async
  // floor-estimation effect has a result, so this keeps the card up to date.
  const currentMapCardData = useSelector(
    (state: { mapCard: { mapCardData: Record<string, unknown> | null } }) =>
      state.mapCard.mapCardData,
  );
  useEffect(() => {
    if (isCardOpen && currentMapCardData) {
      dispatch(
        setMapCardData({
          ...currentMapCardData,
          estimatedCallFloor,
        }),
      );
    }
    // Only re-run when estimatedCallFloor changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedCallFloor]);

  // Reset ground elevation when building changes
  useEffect(() => {
    setGroundElevation(null);
  }, [selectedBuilding]);

  const handleClick = useCallback(
    (info: DeckPickingInfo) => {
      if (info.object && info.object.properties) {
        const props = info.object.properties;
        const buildingKey = getBuildingKey(props);
        const siteId = props.site_id;

        const isSameBuilding = isDetailMode && selectedBuilding === buildingKey;
        const willFetch = siteId && (!isSameBuilding || !geoJSONData);

        setSelectedBuilding(buildingKey);
        setIsDetailMode(true);

        // Fetch GeoJSON if we have a site_id and it's a different building
        if (willFetch) {
          // Check cache first to avoid network request
          const cachedData = geoJSONCache.get(siteId);
          if (cachedData) {
            setGeoJSONData(cachedData);
          } else {
            setIsLoadingGeoJSON(true);
            fetchBuildingGeoJSON(siteId)
              .then(data => {
                geoJSONCache.set(siteId, data); // Cache the result
                setGeoJSONData(data);
              })
              .finally(() => {
                setIsLoadingGeoJSON(false);
              });
          }
        }

        const floorsMap = buildingFloorsRef.current.get(buildingKey);
        const floors = floorsMap
          ? Array.from(floorsMap.values()).sort(
              (a, b) => a.floor_level - b.floor_level,
            )
          : [
              {
                floor_level: props.floor_level,
                floor_label: props.floor_label || `Floor ${props.floor_level}`,
              },
            ];

        const lowestFloorLevel =
          floors.length > 0
            ? Math.min(...floors.map(f => f.floor_level))
            : props.floor_level;

        const floorToSelect =
          isSameBuilding && selectedFloor !== null
            ? selectedFloor
            : lowestFloorLevel;

        dispatch(
          setMapCardData({
            layerData: {
              source: 'Pure Wireless',
              title: props.site_name || 'Indoor Map',
            },
            spatialObject: {
              properties: props,
              floors: floors,
            },
            selectedFloor: floorToSelect,
            estimatedCallFloor,
          }),
        );
        dispatch(setCardsToRender(PURE_WIRELESS_CARD));
      }
      return true;
    },
    [
      dispatch,
      isDetailMode,
      selectedBuilding,
      selectedFloor,
      geoJSONData,
      estimatedCallFloor,
    ],
  );

  const trackFloorFromProps = useCallback((props: TileProperties) => {
    if (props && props.floor_level !== undefined) {
      const bKey = getBuildingKey(props);
      if (!buildingFloorsRef.current.has(bKey)) {
        buildingFloorsRef.current.set(bKey, new Map());
      }
      const floorsMap = buildingFloorsRef.current.get(bKey);
      if (floorsMap && !floorsMap.has(props.floor_level)) {
        floorsMap.set(props.floor_level, {
          floor_level: props.floor_level,
          floor_label: props.floor_label || `Floor ${props.floor_level}`,
        });
      }
    }
  }, []);

  // Handle click on a room/polygon in GeoJSON detail mode
  // Updates the card with room-specific data while preserving building info
  const handleRoomClick = useCallback(
    (info: DeckPickingInfo) => {
      if (info.object && info.object.properties) {
        const props = info.object.properties;
        const buildingKey = getBuildingKey(props);

        // Get existing floors for this building
        const floorsMap = buildingFloorsRef.current.get(buildingKey);
        const floors = floorsMap
          ? Array.from(floorsMap.values()).sort(
              (a, b) => a.floor_level - b.floor_level,
            )
          : [
              {
                floor_level: props.floor_level,
                floor_label: props.floor_label || `Floor ${props.floor_level}`,
              },
            ];

        // Highlight the clicked room
        setSelectedRoomFeature(info.object as GeoJSON.Feature);

        // Update card with the clicked room's properties
        dispatch(
          setMapCardData({
            layerData: {
              source: 'Pure Wireless',
              title: props.site_name || 'Indoor Map',
            },
            spatialObject: {
              properties: props,
              floors: floors,
            },
            selectedFloor: selectedFloor, // Keep current floor selection
            estimatedCallFloor,
          }),
        );
      }
      return true;
    },
    [dispatch, selectedFloor, estimatedCallFloor],
  );

  // Derive detail-mode and geoJSONData from isCardOpen so that when
  // closeAllCards() fires, consumers see isDetailMode=false in the SAME
  // render — preventing an expensive intermediate rebuild of ESRI layers
  // for all floors (selectedFloor goes null before local state clears).
  const effectiveDetailMode = isDetailMode && isCardOpen;
  const effectiveGeoJSONData = isCardOpen ? geoJSONData : null;

  return {
    selectedBuilding,
    isDetailMode: effectiveDetailMode,
    hoverInfo,
    selectedFloor,
    isVisible,
    isCardOpen,
    geoJSONData: effectiveGeoJSONData,
    isLoadingGeoJSON,
    isEstimatingFloor,
    isCallInsideBuilding,
    estimatedCallFloor,
    groundElevation,
    selectedRoomFeature: isCardOpen ? selectedRoomFeature : null,
    buildingFloorsRef,
    setSelectedBuilding,
    setIsDetailMode,
    setHoverInfo,
    setGeoJSONData,
    handleClick,
    handleRoomClick,
    trackFloorFromProps,
    dispatch,
  };
};

// ============================================================================
// GeoJSON Detail Layers (fetched from API - no tile boundary artifacts)
// ============================================================================

// GeoJSON detail layers interface
interface CreateGeoJSONDetailLayersOptions {
  geoJSONData: BuildingGeoJSONData;
  selectedFloor: number | null;
  textColor: [number, number, number, number];
  /** RGBA outline/halo color for text labels. Requires fontSettings.sdf. */
  textOutlineColor?: [number, number, number, number];
  /** Width of the text outline relative to font size (0–1). Only effective when textOutlineColor is set. */
  textOutlineWidth?: number;
  showPoints?: boolean;
  onClickRef?: React.MutableRefObject<(info: DeckPickingInfo) => boolean>;
  /** If true, highlight the floor outline to indicate the call is on this floor */
  highlightCallFloor?: boolean;
  /** RGBA color for the call-floor highlight outline (e.g. theme primary_base). Defaults to orange if not provided. */
  callFloorHighlightColor?: [number, number, number, number];
  /** GeoJSON feature to highlight as selected (clicked room) */
  selectedRoomFeature?: GeoJSON.Feature | null;
  /** Current Google Maps zoom level for label/point visibility gating.
   *  deck.gl minZoom is NOT evaluated by GoogleMapsOverlay, so we gate manually.
   *  When undefined, labels/points default to visible. */
  currentZoom?: number;
}

export const createGeoJSONDetailLayers = ({
  geoJSONData,
  selectedFloor,
  textColor,
  showPoints = false,
  onClickRef,
  highlightCallFloor = false,
  callFloorHighlightColor,
  selectedRoomFeature,
  currentZoom,
}: CreateGeoJSONDetailLayersOptions): DeckLayerWithId[] => {
  // Compute label/point visibility from currentZoom.
  // When currentZoom is undefined (map not ready, or ESRI which handles its
  // own minScale) labels/points default to visible.
  const showRoomLabels =
    currentZoom === undefined || currentZoom >= ROOM_LABEL_MIN_ZOOM;
  const showPointMarkers =
    currentZoom === undefined || currentZoom >= POINT_MARKER_MIN_ZOOM;
  const showPointLabels =
    currentZoom === undefined || currentZoom >= POINT_LABEL_MIN_ZOOM;

  const highlightColor = callFloorHighlightColor ?? CALL_FLOOR_HIGHLIGHT_COLOR;
  const layers: DeckLayerWithId[] = [];

  // Helper to filter features by floor level
  const filterByFloor = (
    features: GeoJSON.Feature[] | undefined,
  ): GeoJSON.Feature[] => {
    if (!features) return [];
    if (selectedFloor === null) return features;
    return features.filter(
      f => f.properties && f.properties.floor_level === selectedFloor,
    );
  };

  // Footprint layer (building outline) - no stroke to eliminate grid lines
  if (geoJSONData.footprint?.features) {
    const footprintFeatures = filterByFloor(geoJSONData.footprint.features);
    if (footprintFeatures.length > 0) {
      layers.push(
        new GeoJsonLayer({
          id: 'pure-wireless-geojson-footprint',
          data: footprintFeatures,
          filled: true,
          stroked: false, // No outline to eliminate grid lines
          getFillColor: [204, 204, 204, 150],
          parameters: {
            depthTest: false,
          },
          pickable: false,
        }),
      );

      // Add highlighted outline when call is on this floor
      if (highlightCallFloor) {
        layers.push(
          new GeoJsonLayer({
            id: 'pure-wireless-geojson-footprint-highlight',
            data: footprintFeatures,
            filled: false,
            stroked: true,
            getLineColor: highlightColor,
            getLineWidth: CALL_FLOOR_HIGHLIGHT_WIDTH,
            lineWidthUnits: 'pixels',
            lineWidthMinPixels: 3,
            lineWidthMaxPixels: 6,
            parameters: {
              depthTest: false,
            },
            pickable: false,
          }),
        );
      }
    }
  }

  // Polygon layer (rooms/spaces)
  if (geoJSONData.polygon?.features) {
    const polygonFeatures = filterByFloor(geoJSONData.polygon.features);
    if (polygonFeatures.length > 0) {
      layers.push(
        new GeoJsonLayer({
          id: 'pure-wireless-geojson-polygons',
          data: polygonFeatures,
          filled: true,
          stroked: true,
          getLineColor: [0, 0, 0, 255], // light gray outline
          getLineWidth: 0.15,
          lineWidthMinPixels: 1,
          getFillColor: (f: unknown) => getRoomFillColor(f as TileFeature),
          parameters: {
            depthTest: false,
          },
          pickable: true,
          autoHighlight: true,
          highlightColor: [255, 255, 0, 100],
          onClick: onClickRef
            ? (info: unknown) => {
                const pickInfo = info as DeckPickingInfo;
                if (pickInfo.object) {
                  return onClickRef.current(pickInfo);
                }
                return false;
              }
            : undefined,
        }),
      );

      // Selected room highlight overlay
      if (selectedRoomFeature) {
        layers.push(
          new GeoJsonLayer({
            id: 'pure-wireless-geojson-selected-room',
            data: [selectedRoomFeature],
            filled: true,
            stroked: true,
            getFillColor: SELECTED_ROOM_HIGHLIGHT,
            getLineColor: SELECTED_ROOM_OUTLINE,
            getLineWidth: 0.2,
            lineWidthMinPixels: 1,
            parameters: { depthTest: false },
            pickable: false,
          }),
        );
      }

      // Add labels for polygons with labels
      const labelFeatures = polygonFeatures.filter(f =>
        getFeatureLabel(f as TileFeature),
      );
      if (labelFeatures.length > 0) {
        // Shadow layer (same size, offset down-right)
        layers.push(
          new TextLayer({
            id: `pure-wireless-geojson-labels-shadow-${selectedFloor}`,
            data: labelFeatures,
            visible: showRoomLabels,
            getPosition: (f: unknown) =>
              getPolygonCentroid(f as PolygonFeature),
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: [0, 0, 0, 160],
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            getPixelOffset: [1, 1],
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 12,
            sizeMaxPixels: 22,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
        // Main label layer
        layers.push(
          new TextLayer({
            id: `pure-wireless-geojson-labels-${selectedFloor}`,
            data: labelFeatures,
            visible: showRoomLabels,
            getPosition: (f: unknown) =>
              getPolygonCentroid(f as PolygonFeature),
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: textColor,
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 12,
            sizeMaxPixels: 22,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
      }
    }
  }

  if (showPoints && geoJSONData.point?.features) {
    const pointFeatures = filterByFloor(geoJSONData.point.features);
    if (pointFeatures.length > 0) {
      layers.push(
        new IconLayer({
          id: 'pure-wireless-geojson-points',
          data: pointFeatures,
          visible: showPointMarkers,
          getPosition: (f: unknown) => {
            const feature = f as GeoJSON.Feature<GeoJSON.Point>;
            return feature.geometry.coordinates as [number, number];
          },
          getIcon: () => INDOOR_POINT_MARKER_ICON,
          getSize: 12,
          sizeMinPixels: 8,
          sizeMaxPixels: 16,
          pickable: true,
        }),
      );

      // Labels for point features
      const labeledPoints = pointFeatures.filter(f =>
        getFeatureLabel(f as TileFeature),
      );
      if (labeledPoints.length > 0) {
        // Shadow layer
        layers.push(
          new TextLayer({
            id: `pure-wireless-geojson-point-labels-shadow-${selectedFloor}`,
            data: labeledPoints,
            visible: showPointLabels,
            getPosition: (f: unknown) => {
              const feature = f as GeoJSON.Feature<GeoJSON.Point>;
              return feature.geometry.coordinates as [number, number];
            },
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: [0, 0, 0, 160],
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'top',
            getPixelOffset: [1, 9],
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 10,
            sizeMaxPixels: 20,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
        // Main label layer
        layers.push(
          new TextLayer({
            id: `pure-wireless-geojson-point-labels-${selectedFloor}`,
            data: labeledPoints,
            visible: showPointLabels,
            getPosition: (f: unknown) => {
              const feature = f as GeoJSON.Feature<GeoJSON.Point>;
              return feature.geometry.coordinates as [number, number];
            },
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: textColor,
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'top',
            getPixelOffset: [0, 8],
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 10,
            sizeMaxPixels: 20,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
      }
    }
  }

  return layers;
};

// ============================================================================
// MVT Layer (for building footprint outlines only)
// ============================================================================

interface CreateMVTLayerOptions {
  buildingFloorsRef: React.MutableRefObject<
    Map<string, Map<number, FloorInfo>>
  >;
  onClickRef: React.MutableRefObject<(info: DeckPickingInfo) => boolean>;
  onHoverRef?: React.MutableRefObject<(info: DeckPickingInfo) => void>;
  visible?: boolean;
}

/**
 * Creates an MVT layer that displays building footprint outlines only.
 * The layer is always kept loaded to avoid re-fetching tiles.
 * Visibility is toggled based on whether we're in detail mode or not.
 */
export const createPureWirelessMVTLayer = ({
  buildingFloorsRef,
  onClickRef,
  onHoverRef,
  visible = true,
}: CreateMVTLayerOptions) => {
  return new MVTLayer<TileProperties>({
    id: 'pure-wireless-mvt',
    data: PURE_WIRELESS_URL,
    minZoom: 14,
    maxZoom: 22,
    binary: false,
    visible, // Toggle visibility instead of recreating layer

    // Filter out point features and outdoor_point layer — only render polygons
    renderSubLayers: (props: { data?: TileFeature[] }) => {
      if (props.data && Array.isArray(props.data)) {
        const filteredData = props.data.filter(
          (f: TileFeature) =>
            f.geometry?.type !== 'Point' &&
            f.properties?.layerName !== OUTDOOR_POINT_LAYER_NAME,
        );
        return new GeoJsonLayer({
          ...props,
          data: filteredData,
        });
      }
      return new GeoJsonLayer(props);
    },

    // Show only footprint features (building outlines)
    getFillColor: (f: TileFeature) => {
      const spaceType = f.properties?.space_type?.toLowerCase();
      if (spaceType === 'footprint') {
        return FOOTPRINT_FILL_COLOR;
      }
      return [0, 0, 0, 0]; // Hide non-footprint features
    },
    getLineColor: (f: TileFeature) => {
      const spaceType = f.properties?.space_type?.toLowerCase();
      if (spaceType === 'footprint') {
        return FOOTPRINT_OUTLINE_COLOR;
      }
      return [0, 0, 0, 0]; // Hide non-footprint features
    },
    getLineWidth: 2,
    lineWidthMinPixels: 2,
    getPointRadius: 0,
    pointRadiusMinPixels: 0,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,

    onHover: onHoverRef
      ? (info: unknown) => {
          const pickInfo = info as DeckPickingInfo;
          onHoverRef.current(pickInfo);
        }
      : undefined,

    onClick: (info: unknown) => {
      const pickInfo = info as DeckPickingInfo;
      if (pickInfo.object) {
        const props = pickInfo.object.properties;
        if (props && props.floor_level !== undefined) {
          const bKey = getBuildingKey(props);
          if (!buildingFloorsRef.current.has(bKey)) {
            buildingFloorsRef.current.set(bKey, new Map());
          }
          const floorsMap = buildingFloorsRef.current.get(bKey);
          if (floorsMap && !floorsMap.has(props.floor_level)) {
            floorsMap.set(props.floor_level, {
              floor_level: props.floor_level,
              floor_label: props.floor_label || `Floor ${props.floor_level}`,
            });
          }
        }
        return onClickRef.current(pickInfo);
      }
      return false;
    },

    onTileLoad: (tile: MVTTile) => {
      if (tile.content && Array.isArray(tile.content)) {
        tile.content.forEach((f: TileFeature) => {
          const props = f.properties;
          if (props && props.floor_level !== undefined) {
            const bKey = getBuildingKey(props);
            if (!buildingFloorsRef.current.has(bKey)) {
              buildingFloorsRef.current.set(bKey, new Map());
            }
            const floorsMap = buildingFloorsRef.current.get(bKey);
            if (floorsMap && !floorsMap.has(props.floor_level)) {
              floorsMap.set(props.floor_level, {
                floor_level: props.floor_level,
                floor_label: props.floor_label || `Floor ${props.floor_level}`,
              });
            }
          }
        });
      }
    },
  } as unknown as Record<string, unknown>);
};

// Outdoor Points MVT Layer

interface CreateOutdoorPointsLayerOptions {
  visible?: boolean;
  textColor?: [number, number, number, number];
  /** Theme key appended to the layer id so deck.gl treats a theme switch as a new layer, forcing a visual repaint. */
  themeKey?: string;
}

/**
 * Creates an MVT layer that renders only features from the `outdoor_point`
 * vector-tile layer as red triangle markers (same style as indoor points).
 * Labels are always visible alongside the triangle markers.
 * Always visible — independent of detail mode and the floor picker.
 */
export const createOutdoorPointsMVTLayer = ({
  visible = true,
  textColor = [51, 51, 51, 255],
  themeKey = '',
}: CreateOutdoorPointsLayerOptions = {}) => {
  // Include themeKey in id so deck.gl treats a theme switch as a new layer,
  // forcing a full visual repaint (overlay only redraws on viewport or layer-id change).
  const layerId = themeKey
    ? `pure-wireless-outdoor-points-${themeKey}`
    : 'pure-wireless-outdoor-points';
  return new MVTLayer<TileProperties>({
    id: layerId,
    data: PURE_WIRELESS_URL,
    minZoom: 14,
    maxZoom: 22,
    binary: false,
    visible,
    renderSubLayers: (props: {
      id?: string;
      data?: TileFeature[];
      modelMatrix?: unknown;
      coordinateOrigin?: unknown;
      coordinateSystem?: unknown;
    }) => {
      if (!props.data || !Array.isArray(props.data)) return null;

      const outdoorData = props.data.filter(
        (f: TileFeature) =>
          f.properties?.layerName === OUTDOOR_POINT_LAYER_NAME,
      );
      if (outdoorData.length === 0) return null;

      // Cherry-pick tile props only; spreading full ...props breaks TextLayer getColor on ESRI overlay.
      const tilePropsRaw = props as Record<string, unknown>;
      const tileProps: Record<string, unknown> = {};
      if (tilePropsRaw.modelMatrix !== undefined)
        tileProps.modelMatrix = tilePropsRaw.modelMatrix;
      if (tilePropsRaw.coordinateOrigin !== undefined)
        tileProps.coordinateOrigin = tilePropsRaw.coordinateOrigin;
      if (tilePropsRaw.coordinateSystem !== undefined)
        tileProps.coordinateSystem = tilePropsRaw.coordinateSystem;

      const sublayers: unknown[] = [];

      sublayers.push(
        new IconLayer({
          ...tileProps,
          id: `${props.id}-outdoor-icons`,
          data: outdoorData,
          getPosition: (f: unknown) => {
            const feature = f as GeoJSON.Feature<GeoJSON.Point>;
            return feature.geometry.coordinates as [number, number];
          },
          getIcon: () => POINT_MARKER_TRIANGLE_ICON,
          getSize: 12,
          sizeMinPixels: 8,
          sizeMaxPixels: 16,
          pickable: true,
          autoHighlight: true,
          highlightColor: HIGHLIGHT_COLOR,
        }),
      );

      const labeledFeatures = outdoorData.filter(f =>
        getFeatureLabel(f as TileFeature),
      );
      if (labeledFeatures.length > 0) {
        // Main label first: ESRI DeckOverlay draws later sublayers underneath.
        sublayers.push(
          new TextLayer({
            ...tileProps,
            id: `${props.id}-outdoor-labels`,
            data: labeledFeatures,
            getPosition: (f: unknown) => {
              const feature = f as GeoJSON.Feature<GeoJSON.Point>;
              return feature.geometry.coordinates as [number, number];
            },
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: textColor,
            updateTriggers: { getColor: textColor },
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'top',
            getPixelOffset: [0, 8],
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 10,
            sizeMaxPixels: 20,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
        sublayers.push(
          new TextLayer({
            ...tileProps,
            id: `${props.id}-outdoor-labels-shadow`,
            data: labeledFeatures,
            getPosition: (f: unknown) => {
              const feature = f as GeoJSON.Feature<GeoJSON.Point>;
              return feature.geometry.coordinates as [number, number];
            },
            getText: (f: unknown) => getFeatureLabel(f as TileFeature),
            getSize: 14,
            getColor: OUTDOOR_LABEL_SHADOW,
            updateTriggers: { getColor: OUTDOOR_LABEL_SHADOW },
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'top',
            getPixelOffset: [1, 9],
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold',
            sizeMinPixels: 10,
            sizeMaxPixels: 20,
            sizeUnits: 'pixels',
            pickable: false,
            fontSettings: { fontSize: 128 },
          }),
        );
      }

      return sublayers;
    },

    pickable: true,
  } as unknown as Record<string, unknown>);
};

export type {
  FloorInfo,
  TileProperties,
  PolygonFeature,
  PointFeature,
  TileFeature,
  MapCardState,
  BuildingGeoJSONData,
};
